import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Polyline, Popup } from "react-leaflet";

// Actions
import { getCampaignPlan } from "../../../actions/campaign-plan/CampaignPlanActions";
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { getRoadStretchesByCityId } from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";

// Constants and Utils
import {
  DATE_FORMATS,
  RoadStretchesPathOptionsConstants,
} from "../../../constants/GeneralConstants";
import {
  getDifferenceInDays,
  getTimestamp,
} from "../../../common-utils/date-utils/DateUtils";
import { getInitialDateAndDuration } from "../../utils/campaignPlanningUtil";
import {
  constructStretchesDropdownItems,
  getMapCenterAndZoomFromStretch,
  getPlanStretchesMap,
} from "./CampaignCityStretchesMetricsUtils";
import { StretchDayWiseMetrics } from "./CampaignCityStretchesMetricsConstants";

// Components
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import { CampaignDetails } from "../components/HeaderNav";
import LLMap from "../../../components/map/leaflet-map/LLMap";
import { PlainDropdown } from "../../../components/dropdown/Dropdown";
import BootstrapDateRangePicker from "../../../components/bootstrap-date-range-picker/BootstrapDateRangePicker";
import { ButtonWithLoader } from "../../../mavin/components/button/Button";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";
import TrafficFlowArrow from "../../../components/map/traffic-flow-arrow/TrafficFlowArrow";
import StretchMetricsInfoAndChart from "./StretchMetricsInfoAndChart";
import StretchMetricsProgressTable from "./StretchMetricsProgressTable";

// Page Components
function StretchSelectSection({
  campaignPlan,
  roadStretchMap,
  stretchId,
  setStretchId,
}) {
  const { roadStretchOtsMap } = campaignPlan || {};
  const planSelectedStretchIds = Object.keys(roadStretchOtsMap || {});

  const selectedStretches = constructStretchesDropdownItems(
    planSelectedStretchIds,
    roadStretchMap
  );

  return (
    <div className="form-group row">
      <label className="col-3 col-form-label">{"Stretch Name"}</label>
      <div className="col-9">
        <PlainDropdown
          items={selectedStretches}
          className="btn shadow-none rounded-lg border px-3"
          placeHolder="Select Stretch"
          selectedId={stretchId}
          onItemSelect={(id) => setStretchId(id)}
          dropMenuClassName="dropdown-fixed-height"
          isFirstItemSelectable={true}
        />
      </div>
    </div>
  );
}

function TargetGroupSection({ campaignPlan }) {
  const { tgAttrMap = {}, targetGroupId } = campaignPlan || {};
  const allTgSelections = Object.values(tgAttrMap)
    .flat()
    .map((item) => item.tgAttrName);

  return (
    <div className="form-group row align-items-baseline pt-4">
      <label className="col-3 col-form-label">{"Target Group"}</label>
      <div className="col-9 d-flex flex-wrap">
        {!targetGroupId && <p className="mb-0 text-muted">Not Selected</p>}
        {targetGroupId &&
          allTgSelections.map((name, i) => (
            <span
              key={i}
              className="mr-2 mb-2 px-2 py-1 bg-alt border rounded-lg"
            >
              {name}
            </span>
          ))}
      </div>
    </div>
  );
}

function DateAndDurationSection({
  campaignPlan,
  duration,
  setDuration,
  datesObj,
  setDatesObj,
}) {
  const { startTimestamp, endTimestamp } = campaignPlan || {};
  const {
    startDateObj,
    endDateObj,
    duration: planDuration,
  } = getInitialDateAndDuration(startTimestamp, endTimestamp);

  // duration string
  const durationString = duration ? `( ${duration} Days )` : "";

  // update dates
  useEffect(() => {
    setDuration(planDuration);
    setDatesObj({ startDateObj, endDateObj });
  }, [JSON.stringify(campaignPlan)]);

  // Functions
  function onDateSelect(selectedDate) {
    const { startDate, endDate } = selectedDate || {};
    const startTs = getTimestamp(startDate, DATE_FORMATS.date_month_year);
    const endTs = getTimestamp(endDate, DATE_FORMATS.date_month_year);
    const dayCount = getDifferenceInDays(startTs, endTs) - 1;

    // update states
    setDuration(dayCount);
    setDatesObj({
      startDateObj: startDate,
      endDateObj: endDate,
    });
  }

  // DatePicker Style
  const styleObject = {
    autoApply: true,
    border: true,
    buttonClassName: "min-w-150 px-0",
    timePicker: true,
  };

  return (
    <div className="form-group row pt-4">
      <label className="col-3 col-form-label">{"Campaign Date"}</label>
      <div className="col-9">
        <BootstrapDateRangePicker
          initialStartDate={datesObj.startDateObj}
          initialEndDate={datesObj.endDateObj}
          onApplyDates={onDateSelect}
          styleObject={styleObject}
        />

        <span className="mb-0 mx-3">{durationString}</span>
      </div>
    </div>
  );
}

function FormAndMapSection({
  campaignPlan = {},
  roadStretchMap = {},
  setStretchMetrics,
}) {
  // States
  const [stretchId, setStretchId] = useState("");
  const [duration, setDuration] = useState("");
  const [datesObj, setDatesObj] = useState({
    startDateObj: {},
    endDateObj: {},
  });

  // plan stretches map
  const planStretchesMap = getPlanStretchesMap(campaignPlan, roadStretchMap);

  // Map Coordinates
  const { center, zoom } = getMapCenterAndZoomFromStretch(
    roadStretchMap,
    stretchId
  );

  // function
  function handleGetMetrics() {
    // TODO::remove when integrating with api response
    setStretchMetrics(StretchDayWiseMetrics);
  }

  // path options constants
  const { selected, unSelected } = RoadStretchesPathOptionsConstants;

  return (
    <div className="d-flex min-h-400">
      {/* Form */}
      <div className="col-6 pl-0">
        <StretchSelectSection
          campaignPlan={campaignPlan}
          roadStretchMap={roadStretchMap}
          stretchId={stretchId}
          setStretchId={setStretchId}
        />
        <DateAndDurationSection
          campaignPlan={campaignPlan}
          duration={duration}
          setDuration={setDuration}
          datesObj={datesObj}
          setDatesObj={setDatesObj}
        />
        <TargetGroupSection campaignPlan={campaignPlan} />

        <div className="text-right mt-5">
          <ButtonWithLoader
            displayContent={"Get Stretch Metrics"}
            onClickFunction={handleGetMetrics}
            loader={false}
            isDisabled={false}
          />
        </div>
      </div>

      {/* Map */}
      <div className="col-6">
        <LLMap center={[center[0], center[1]]} zoom={zoom}>
          {Object.values(planStretchesMap).map((eachStretch, i) => {
            const { name, trace, id } = eachStretch;
            const isSelected = id === stretchId;
            const pathOption = isSelected ? selected : unSelected;

            return (
              <Fragment key={i}>
                <Polyline pathOptions={pathOption} positions={trace}>
                  <Popup>
                    <p>{name}</p>
                  </Popup>
                </Polyline>
                <TrafficFlowArrow trace={trace} />
              </Fragment>
            );
          })}
        </LLMap>
      </div>
    </div>
  );
}

/**
 * Campaign City Stretches Metrics Page
 */
export default function CampaignCityStretchesMetricsPage() {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // state
  const [stretchMetrics, setStretchMetrics] = useState({});

  // Get campaignPlan
  const campaignPlan = useSelector((state) => state.campaignPlan.campaignPlan);
  const { isDigital } = campaignPlan || {};

  // Get campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  // City road stretches
  const roadStretchMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // Loadings
  const campaignPlanLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanLoading
  );
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const roadStretchesByCityIdLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );

  useEffect(() => {
    dispatch(getCampaignPlan(campaignId, cityId));
    dispatch(getBasicCampaignDetails(campaignId));
  }, [dispatch, campaignId, cityId]);

  // road stretches
  useEffect(() => {
    if (Object.keys(campaignPlan).length > 0) {
      dispatch(getRoadStretchesByCityId(cityId, "", isDigital));
    }
  }, [dispatch, JSON.stringify(campaignPlan)]);

  // Sidebar Settings
  const citySelected = { cityId };
  const redirectLinkObj = {
    campaignOverviewLink: `/campaign/${campaignId}/confirm`,
    citySummaryLink: `/campaign/${campaignId}/city/:cityId/confirm`,
  };

  // Page Title
  const pageTitle = <CampaignDetails campaign={campaign} />;

  // Page Loading
  const isPageLoading = campaignPlanLoading || basicCampaignDetailsLoading;
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar
        campaign={campaign}
        citySelected={citySelected}
        redirectLinkObj={redirectLinkObj}
        isHideOverview={true}
      />

      <div className="content-wrapper">
        <PageHeader title={pageTitle} shadow={true} />

        <div className="page-content">
          {roadStretchesByCityIdLoading && (
            <AlertMessage
              className="justify-content-center p-3 my-4"
              textMessage="Road Stretches Loading, Please wait.."
            />
          )}
          {!roadStretchesByCityIdLoading && (
            <>
              <FormAndMapSection
                campaignPlan={campaignPlan}
                roadStretchMap={roadStretchMap}
                setStretchMetrics={setStretchMetrics}
              />
              <hr className="divider"></hr>
            </>
          )}

          {/* metrics info and chart */}
          <StretchMetricsInfoAndChart stretchMetrics={stretchMetrics} />

          {/* Metrics Progress Table */}
          <StretchMetricsProgressTable stretchMetrics={stretchMetrics} />
        </div>
      </div>
    </>
  );
}
